import { memo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

import type { EmptyStateProps, EmptyStateSize } from './EmptyState.types';
import type { Theme } from '../../theme';

/**
 * Fade-in animation for smooth appearance
 */
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Size configuration for padding and spacing
 */
const sizeConfig: Record<EmptyStateSize, { padding: string; gap: string; iconSize: string }> = {
  sm: { padding: '24px 16px', gap: '8px', iconSize: '32px' },
  md: { padding: '48px 24px', gap: '12px', iconSize: '48px' },
  lg: { padding: '64px 32px', gap: '16px', iconSize: '64px' },
};

/**
 * Container for the empty state
 */
const EmptyStateContainer = styled.div<{
  theme?: Theme;
  $size: EmptyStateSize;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ $size }) => sizeConfig[$size].padding};
  text-align: center;
  flex: 1;
  animation: ${fadeIn} 0.3s ease-out;
`;

/**
 * Icon container
 */
const IconContainer = styled.div<{
  theme?: Theme;
  $size: EmptyStateSize;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ $size }) => sizeConfig[$size].gap};
  color: ${({ theme }) => theme.colors.text.secondary};
  
  /* Scale icon if it's an SVG or img */
  & > svg,
  & > img {
    width: ${({ $size }) => sizeConfig[$size].iconSize};
    height: ${({ $size }) => sizeConfig[$size].iconSize};
  }
`;

/**
 * Title text
 */
const Title = styled.h3<{
  theme?: Theme;
  $size: EmptyStateSize;
}>`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme, $size }) =>
    $size === 'lg'
      ? theme.typography.fontSize.lg
      : $size === 'md'
      ? theme.typography.fontSize.md
      : theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

/**
 * Description text - always uses xs font size for consistency
 */
const Description = styled.p<{
  theme?: Theme;
  $size: EmptyStateSize;
}>`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  margin-bottom: ${({ theme }) => theme.spacing[5]};
  max-width: 280px;
`;

/**
 * Action container
 */
const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

/**
 * EmptyState - A component for displaying empty state messages
 *
 * Use when there's no content to show - search results, lists, etc.
 * Provides a centered layout with optional icon, title, description, and action.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <EmptyState
 *   title="No results found"
 *   description="Try adjusting your search or filters"
 * />
 *
 * // With icon and action
 * <EmptyState
 *   icon={<Icon name="search" size="lg" />}
 *   title="No reviews yet"
 *   description="Submit a file for review to see results here"
 *   action={<Button variant="primary">Create Review</Button>}
 * />
 *
 * // Different sizes
 * <EmptyState
 *   title="Empty"
 *   size="sm"
 * />
 * ```
 */
const EmptyStateComponent = ({
  icon,
  title,
  description,
  action,
  size = 'md',
  className,
  'data-testid': testId,
}: EmptyStateProps) => {
  return (
    <EmptyStateContainer
      className={className}
      $size={size}
      data-testid={testId}
    >
      {icon && (
        <IconContainer $size={size} data-testid={testId && `${testId}-icon`}>
          {icon}
        </IconContainer>
      )}
      <Title $size={size} data-testid={testId && `${testId}-title`}>
        {title}
      </Title>
      {description && (
        <Description $size={size} data-testid={testId && `${testId}-description`}>
          {description}
        </Description>
      )}
      {action && (
        <ActionContainer data-testid={testId && `${testId}-action`}>
          {action}
        </ActionContainer>
      )}
    </EmptyStateContainer>
  );
};

EmptyStateComponent.displayName = 'EmptyState';

export const EmptyState = memo(EmptyStateComponent);

export default EmptyState;
